/**
 * Voice Conversation Types
 *
 * Core type definitions for the full-duplex voice conversation feature.
 * Covers state management, usage/cost tracking, model configuration,
 * and the pluggable STT provider interface.
 */

// ---------------------------------------------------------------------------
// Voice Conversation State
// ---------------------------------------------------------------------------

/** Voice conversation engine state machine */
export type VoiceConversationState =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'processing'
  | 'speaking'
  | 'error'

/** Voice conversation interaction mode */
export type VoiceConversationMode = 'push-to-talk' | 'open-mic'

/** Voice conversation engine type */
export type VoiceEngineType = 'pipeline' | 'openai-realtime'

// ---------------------------------------------------------------------------
// Model Configuration
// ---------------------------------------------------------------------------

/** Deepgram STT model used at runtime — always Nova-3 as of May 2026. */
export type DeepgramModel = 'nova-3'

/** Legacy model identifiers preserved for historical turn-usage records only. */
export type LegacyDeepgramModel = 'nova-2' | 'enhanced' | 'base'

/** OpenAI Realtime model options */
export type RealtimeModel = 'gpt-realtime' | 'gpt-realtime-mini'

/** OpenAI Realtime voice options */
export type RealtimeVoice =
  | 'alloy'
  | 'ash'
  | 'ballad'
  | 'coral'
  | 'echo'
  | 'sage'
  | 'shimmer'
  | 'verse'

/** OpenAI Realtime turn detection mode */
export type TurnDetectionMode = 'server_vad' | 'semantic_vad'

/** OpenAI Realtime-specific settings */
export interface RealtimeSettings {
  /** Voice to use for Realtime API responses */
  voice: RealtimeVoice
  /** Turn detection mode: server_vad (threshold) or semantic_vad (intent) */
  turnDetection: TurnDetectionMode
}

/** Configurable model selections — enables cost vs quality comparison */
export interface VoiceModelConfig {
  /** Deepgram STT model (pipeline mode) */
  sttModel: DeepgramModel
  /** OpenAI Realtime model (realtime mode) */
  realtimeModel: RealtimeModel
}

// ---------------------------------------------------------------------------
// Voice Conversation Settings
// ---------------------------------------------------------------------------

/** Audio output format preference */
export type AudioOutputFormat = 'pcm' | 'mp3' | 'adaptive'

/** Full voice conversation settings */
export interface VoiceConversationSettings {
  /** Enable the voice conversation feature */
  enabled: boolean
  /** Voice conversation engine: 'pipeline' or 'openai-realtime' */
  engine: VoiceEngineType
  /** Push-to-talk (default) or open-mic */
  mode: VoiceConversationMode
  /** Auto-send transcript when speech ends (default: true) */
  autoSend: boolean
  /** Show real-time partial transcript while speaking */
  showTranscript: boolean
  /** STT language for pipeline mode */
  sttLanguage: string
  /** TTS output format: 'pcm' (low latency), 'mp3' (low bandwidth), 'adaptive' */
  audioOutputFormat: AudioOutputFormat
  /** Keyboard shortcut for push-to-talk (default: Space) */
  pttShortcut: string
  /** Open-mic: seconds of non-speech before a wordless trigger lets go (2 to 15) */
  openMicNoWordsSeconds: number
  /** Edge-local: quiet audio (ms) that ends an utterance; null = the server's default */
  localSilenceMs: number | null
  /** Edge-local: RMS at or below which audio is quiet; null = the server's default */
  localSilenceThreshold: number | null
  /** Edge-local: audio (ms) gathered before long speech is written out; null = the server's default */
  localMaxBufferMs: number | null
  /** Model selection for cost vs quality analysis */
  models: VoiceModelConfig
  /** OpenAI Realtime-specific settings */
  realtimeSettings: RealtimeSettings
}

/** Default voice conversation settings */
export const DEFAULT_VOICE_CONVERSATION_SETTINGS: VoiceConversationSettings = {
  enabled: true,
  engine: 'pipeline',
  mode: 'push-to-talk',
  autoSend: true,
  showTranscript: true,
  sttLanguage: 'multi',  // was 'en' — Nova-3 multilingual auto-detect
  audioOutputFormat: 'adaptive',
  pttShortcut: 'Space',
  openMicNoWordsSeconds: 4,
  localSilenceMs: null,
  localSilenceThreshold: null,
  localMaxBufferMs: null,
  models: {
    sttModel: 'nova-3',
    realtimeModel: 'gpt-realtime-mini',
  },
  realtimeSettings: {
    voice: 'alloy',
    turnDetection: 'server_vad',
  },
}

/** Open-mic no-words setting: seconds, a whole number from min to max */
export const OPEN_MIC_NO_WORDS_SECONDS = { default: 4, min: 2, max: 15 } as const

/** A whole number from 2 to 15 is itself; anything else reads as the default */
export function readOpenMicNoWordsSeconds(value: unknown): number {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= OPEN_MIC_NO_WORDS_SECONDS.min &&
    value <= OPEN_MIC_NO_WORDS_SECONDS.max
    ? value
    : OPEN_MIC_NO_WORDS_SECONDS.default
}

/** Edge-local Advanced settings (SA-1393): the steps a person may choose */
export const LOCAL_SILENCE_MS_STEPS = [400, 600, 800, 1000, 1200, 1500, 2000, 3000] as const
export const LOCAL_SILENCE_THRESHOLD_STEPS = [50, 100, 200, 400, 800] as const
export const LOCAL_MAX_BUFFER_MS_STEPS = [3000, 5000, 8000, 10000, 15000, 20000] as const

function readStep(steps: readonly number[], value: unknown): number | null {
  return typeof value === 'number' && steps.includes(value) ? value : null
}

/** An offered step is itself; anything else reads as the server's default (null) */
export function readLocalSilenceMs(value: unknown): number | null {
  return readStep(LOCAL_SILENCE_MS_STEPS, value)
}

export function readLocalSilenceThreshold(value: unknown): number | null {
  return readStep(LOCAL_SILENCE_THRESHOLD_STEPS, value)
}

export function readLocalMaxBufferMs(value: unknown): number | null {
  return readStep(LOCAL_MAX_BUFFER_MS_STEPS, value)
}

/** What the local STT server is told for one connection; an unset value keeps its default */
export interface LocalSTTOptions {
  silenceMs?: number
  silenceThreshold?: number
  maxBufferMs?: number
}

/** The person's readable edge-local settings, or undefined when none is set */
export function localSttOptions(
  s: Pick<VoiceConversationSettings, 'localSilenceMs' | 'localSilenceThreshold' | 'localMaxBufferMs'>,
): LocalSTTOptions | undefined {
  const options: LocalSTTOptions = {}
  const silenceMs = readLocalSilenceMs(s.localSilenceMs)
  const silenceThreshold = readLocalSilenceThreshold(s.localSilenceThreshold)
  const maxBufferMs = readLocalMaxBufferMs(s.localMaxBufferMs)
  if (silenceMs !== null) options.silenceMs = silenceMs
  if (silenceThreshold !== null) options.silenceThreshold = silenceThreshold
  if (maxBufferMs !== null) options.maxBufferMs = maxBufferMs
  return Object.keys(options).length > 0 ? options : undefined
}

// ---------------------------------------------------------------------------
// Per-Turn Cost Tracking
// ---------------------------------------------------------------------------

/** STT usage data for a single turn */
export interface STTUsage {
  provider: string
  model: string
  /** Audio duration processed (ms) — from Deepgram Results.duration */
  audioDurationMs: number
  /** Estimated cost based on pricing table */
  estimatedCost: number
}

/** LLM usage data for a single turn */
export interface LLMUsage {
  provider: string
  model: string
  promptTokens: number
  completionTokens: number
  totalTokens: number
  /** Tokens served from prompt cache */
  cachedTokens?: number
  /** Tokens used for internal reasoning (o-series models) */
  reasoningTokens?: number
  /** OpenAI Realtime: audio input tokens */
  audioInputTokens?: number
  /** OpenAI Realtime: audio output tokens */
  audioOutputTokens?: number
  estimatedCost: number
}

/** TTS usage data for a single turn */
export interface TTSUsage {
  provider: string
  model: string
  /** Characters sent to TTS service (tracked client-side) */
  characterCount: number
  /** Audio duration produced (ms) */
  audioDurationMs: number
  estimatedCost: number
}

/** Complete usage data from a single voice conversation turn */
export interface TurnUsage {
  /** Turn timing */
  turnStartedAt: number
  turnCompletedAt: number

  /** STT usage — pipeline mode only (undefined in realtime mode) */
  stt?: STTUsage

  /** LLM usage — both modes */
  llm?: LLMUsage

  /** TTS usage — pipeline mode only (undefined in realtime mode) */
  tts?: TTSUsage

  /** Total estimated cost for this turn */
  totalEstimatedCost: number

  /** Voice-to-voice latency: speech end → first AI audio byte (ms) */
  latencyMs?: number
}

// ---------------------------------------------------------------------------
// STT Provider Interface (Pluggable)
// ---------------------------------------------------------------------------

/** Configuration for an STT provider connection */
export interface STTConfig {
  /** Ephemeral token for authentication */
  token: string
  /** STT model to use (e.g., 'nova-3', 'nova-2') */
  model?: string
  /** Language code (e.g., 'en', 'es', 'fr') */
  language: string
  /** Enable interim (partial) results */
  interimResults: boolean
  /** VAD-based endpointing sensitivity in ms (0 = disabled) */
  endpointingMs: number
  /** Word-timing-based utterance end detection in ms */
  utteranceEndMs: number
  /** Audio encoding format */
  encoding: 'linear16' | 'opus' | 'flac'
  /** Sample rate in Hz */
  sampleRate: number
  /** Edge-local only: this connection's thresholds (the local server's query string) */
  local?: LocalSTTOptions
}

/** Event callbacks for an STT provider */
export interface STTEvents {
  /**
   * `fromFinalize` is true when this Results message answers a Finalize
   * request (the provider's `from_finalize` flag), not a natural pause.
   */
  onTranscript: (
    text: string,
    isFinal: boolean,
    speechFinal: boolean,
    fromFinalize: boolean
  ) => void
  onUtteranceEnd: () => void
  onError: (error: Error) => void
  onOpen: () => void
  onClose: () => void
}

/** Abstract STT provider interface — supports pluggable backends */
export interface STTProvider {
  readonly name: string

  /**
   * True when the provider answers every Finalize with a `from_finalize`
   * Results message, even when there is no audio left to process. False when
   * the answer is not guaranteed, so callers must not wait for it forever.
   */
  readonly alwaysAnswersFinalize: boolean

  /** Connect to the STT service */
  connect(config: STTConfig): Promise<void>

  /** Send raw audio data for transcription */
  sendAudio(pcmData: ArrayBuffer): void

  /** Send KeepAlive to prevent connection timeout */
  keepAlive(): void

  /** Signal end of audio — flush any buffered results */
  finalize(): void

  /** Gracefully close the connection */
  close(): void

  /** Whether the connection is open */
  readonly isConnected: boolean
}
